import "../JSONTools/JSONformatter.css";
import ToolHeader from "../../Components/ToolHeader";
import "./DifferenceTools.css";
import { Row, Col } from "react-bootstrap";
import { useLocation } from "react-router-dom";
import FormatterPanel from "../../HomePage/FormatterPanel";
import { useEffect, useState } from "react";
import { ReactComponent as CheckGreen } from "../../Icons/checkGreen.svg";
import { ReactComponent as CheckRed } from "../../Icons/CheckRed.svg";
import { ReactComponent as CheckBlue } from "../../Icons/CheckBlue.svg";

function TextMultiDifferenceViewer() {

    const location = useLocation();

    const {
        baseText = "",
        validInputs = [],
        comparisons = {}
    } = location.state || {};

    const results = Object.entries(comparisons || {});

    const [activeIndex, setActiveIndex] = useState(0);

    const activeItemDiff = results[activeIndex]?.[1] || {};

    const activeText = validInputs[activeIndex] || "";

    const [inactiveItems, setInactiveItems] = useState([]);

    useEffect(() => {

        const remaining = validInputs
            .map((content, index) => ({
                content,
                originalIndex: index
            }))
            .filter(item => item.originalIndex !== activeIndex);

        setInactiveItems(remaining);

    }, [activeIndex, validInputs]);

   

    const getLineClassName = (diffType, side) => {
        if (diffType === "ADDED" && side === "compare") return "diff-added-line";
        if (diffType === "REMOVED" && side === "base") return "diff-removed-line";
        if (diffType === "MODIFIED") return "diff-modified-line";
        return "";
    };

    const getSegmentClassName = (segmentType) => {
        if (segmentType === "ADDED") return "text-diff-segment-added";
        if (segmentType === "REMOVED") return "text-diff-segment-removed";
        return "";
    };

    const renderSegments = (segments = [], side) => {
        const visibleSegments = segments.filter((segment) => {
            if (side === "base") return segment.type !== "ADDED";
            return segment.type !== "REMOVED";
        });

        return visibleSegments.map((segment, index) => (
            <span key={index} className={getSegmentClassName(segment.type)}>
                {segment.text}
            </span>
        ));
    };

    const renderDiffLine = (line, diff, side, index) => {
        const lineText = side === "base"
            ? diff?.baseLine ?? line
            : diff?.compareLine ?? line;
        const className = getLineClassName(diff?.diffType, side);
        const shouldRenderSegments = diff?.diffType === "MODIFIED" && diff?.segments?.length;

        return (
            <div key={index} className={`text-diff-line ${className}`}>
                {shouldRenderSegments ? renderSegments(diff.segments, side) : lineText}
            </div>
        );
    };

    const renderHighlightedText = (text, diffData = null, side = "compare") => {
        const lines = String(text || "").split(/\r?\n/);
        const differencesByLine = (diffData?.differences || []).reduce((acc, diff) => {
            acc[diff.lineNumber] = diff;
            return acc;
        }, {});

        if (!text && !Object.keys(differencesByLine).length) {
            return <div className="text-diff-line" />;
        }

        return lines.map((line, index) => {
            const diff = differencesByLine[index + 1];
            return renderDiffLine(line, diff, side, index);
        });
    };

    return (
        <Col>
            <ToolHeader title="Text Comparison Results" description="Compare a base text with multiple text inputs" showFavourite={false} />

            {/* =========================
                SUMMARY CARDS
            ========================= */}

            <Row className="ActionButtons diff-action-buttons">

                {results.map(([key, comp], index) => (

                    <Col
                        key={key}
                        onClick={() => setActiveIndex(index)}
                        className={`ActionButton diff-action-button ${activeIndex === index
                                ? "active"
                                : ""
                            }`}
                        style={{
                            cursor: "pointer",
                            backgroundColor:
                                activeIndex === index
                                    ? "#4F3CFA1A"
                                    : ""
                        }}
                    >

                        <span style={{ color: "var(--text-color)" }}>
                            TEXT {index + 1}
                        </span>

                        <span
                            style={{
                                color: "#21C55E",
                                padding: "4px 8px"
                            }}
                        >
                            {comp.addedCount || 0} Added
                        </span>

                        <span
                            style={{
                                color: "#EF4444",
                                padding: "4px 8px"
                            }}
                        >
                            {comp.removedCount || 0} Removed
                        </span>

                        <span
                            style={{
                                color: "#3B82F6",
                                padding: "4px 8px"
                            }}
                        >
                            {comp.modifiedCount || 0} Modified
                        </span>

                    </Col>

                ))}

            </Row>

            {/* =========================
                LEGEND
            ========================= */}

            <Row className="Introduction-Header align-items-center">

                <Col md={6} className="diff-legend">

                    <span style={{ color: "#21C55E" }}>
                        <CheckGreen /> Added
                    </span>

                    <span style={{ color: "#EF4444" }}>
                        <CheckRed /> Removed
                    </span>

                    <span style={{ color: "#3B82F6" }}>
                        <CheckBlue /> Modified
                    </span>

                </Col>

                <Col
                    md={6}
                    className="DiffoutputToggleWraper d-flex justify-content-end"
                >

                    {validInputs.map((_, idx) => (

                        <div
                            key={idx}
                            className={`DiffoutputToggle ${activeIndex === idx
                                    ? "active"
                                    : ""
                                }`}
                            style={{
                                backgroundColor:
                                    activeIndex === idx
                                        ? "#146EEB"
                                        : "",
                                cursor: "pointer"
                            }}
                            onClick={() => setActiveIndex(idx)}
                        >
                            TEXT {idx + 1}
                        </div>

                    ))}

                </Col>

            </Row>

            {/* =========================
                ACTIVE PANELS
            ========================= */}

            <Row className="Formatter mt-3">

                {/* BASE */}

                <Col md={6}>

                    <FormatterPanel
                        title="Base Text"
                        readOnly
                        customContent={
                            <div
                                className="text-diff-container"
                            >
                                {renderHighlightedText(baseText, activeItemDiff, "base")}
                            </div>
                        }
                        footerLeft={`Characters: ${String(baseText)
                            .replace(/\s/g, "").length}`}
                        footerRight={`Size: ${new Blob([baseText]).size} B`}
                    />

                </Col>

                {/* ACTIVE */}

                <Col md={6}>

                    <FormatterPanel
                        title={`TEXT ${activeIndex + 1}`}
                        readOnly
                        customContent={
                            <div
                                className="text-diff-container"
                            >
                                {renderHighlightedText(activeText, activeItemDiff, "compare")}
                            </div>
                        }
                        footerLeft={`Characters: ${String(activeText)
                            .replace(/\s/g, "").length}`}
                        footerRight={`Size: ${new Blob([activeText]).size} B`}
                    />

                </Col>

            </Row>

            {/* =========================
                INACTIVE PANELS
            ========================= */}

            <Row className="Formatter mt-3">

                {inactiveItems.map((item) => (

                    <Col
                        md={6}
                        key={item.originalIndex}
                        className="mb-3"
                    >

                        <FormatterPanel
                            title={`TEXT ${item.originalIndex + 1}`}
                            readOnly
                            customContent={
                                <div
                                    className="text-diff-container"
                                >
                                    {renderHighlightedText(item.content)}
                                </div>
                            }
                            footerLeft={`Characters: ${String(item.content)
                                .replace(/\s/g, "").length}`}
                            footerRight={`Size: ${new Blob([item.content]).size} B`}
                        />

                    </Col>

                ))}

            </Row>

        </Col>
    );
}

export default TextMultiDifferenceViewer;
